import _ from 'lodash'
import { useSetupStore } from '@/stores'
import { createTree } from '@/nested-set-lib'
import MenuApi from '@/store/api/cms/menu'
import PageApi from '@/store/api/cms/page'

// Wires the canonical quill link tool (src/quill/toolbar/link) into a WhatsApp quill config in
// plain-text mode: it inserts the resolved URL as text (WhatsApp has no link markup), offering
// internal pages, posts (category pages + products), external and email. Shared by the free-form
// composer AND the message-template body editor so the two never drift.
//
// No "media library" type: WhatsApp text has no inline image, so a media URL in the body would
// just be literal text to the customer. Images ride the paperclip (an image message) or the
// interactive header instead. The host's own `<i18n>` block must carry the Insert / External /
// Email / Pages keys (the mixin resolves `$t` against the component that uses it).
export default {
    data() {
        return {
            // Pre-loaded internal-page tree for the link tool. The palette builds its tabs
            // SYNCHRONOUSLY, so an async `pages` source would drop the "internal" tab on the
            // first open — we load it up front and hand the cached array to the module.
            linkPageTree: [],
        }
    },
    computed: {
        // The tenant's storefront base (https://host); prepended to the relative internal/posts
        // paths the link tool produces, so an inserted WhatsApp link is absolute.
        whatsAppLinkStorefrontBase() {
            const base = _.get(useSetupStore().activeCms, 'domains.0.base_url')
            return base ? `https://${base.replace(/^https?:\/\//, '').replace(/\/$/, '')}` : ''
        }
    },
    mounted() {
        this.loadLinkPageTree()
    },
    methods: {
        // Merge the link tool into a base WhatsApp quill config (adds the toolbar button + the
        // module config). The base keeps its own modules (e.g. the template body's placeholder tool).
        withWhatsAppLinkTool(baseConfig) {
            const container = [...baseConfig.modules.toolbar.container, ['link']]
            return {
                ...baseConfig,
                modules: {
                    ...baseConfig.modules,
                    toolbar: { container },
                    link: {
                        ...(baseConfig.modules.link || {}),
                        plainText: true,
                        baseUrl: this.whatsAppLinkStorefrontBase,
                        types: ['internal', 'posts', 'external', 'email'],
                        submit: [{ label: this.$t('Insert') }],
                        pages: () => this.linkPageTree,
                        posts: search => this.searchLinkPosts(search),
                        content: {
                            external: this.$t('External'),
                            email: this.$t('Email'),
                            posts: this.$t('Pages')
                        }
                    }
                }
            }
        },
        // Pre-load the CMS menus as a folder/children tree (relative item paths; baseUrl makes
        // them absolute on insert) into linkPageTree, so the link tool's `internal` tab is
        // populated the first time the palette opens. Same shape the page builder uses.
        loadLinkPageTree() {
            const cms = useSetupStore().activeCms
            if (!cms) return
            MenuApi.fetchList(cms.id)
                .then(menus => {
                    const toNode = item => ({
                        title: item.title,
                        path: item.path,
                        children: (item.children || []).filter(child => child.path).map(toNode)
                    })
                    this.linkPageTree = (menus || [])
                        .map(menu => {
                            const items = createTree(_.cloneDeep((menu.menuItems || []).filter(item => item.path)))
                            const children = items.filter(item => item.path).map(toNode)
                            return children.length ? { folder: menu.name, children } : null
                        })
                        .filter(Boolean)
                })
                .catch(() => { this.linkPageTree = [] })
        },
        // The link tool's `posts` source: content pages (category pages + products), searched by
        // title and scoped to the template's categories. Mirrors InputField.getPagesPaginated.
        searchLinkPosts(searchString = '') {
            const cms = useSetupStore().activeCms
            if (!cms) return Promise.resolve([])
            const categories = _.get(cms, 'config.template.categories') || []
            return PageApi.fetchPagesPaginated({
                filters: [
                    { key: 'title', like: searchString },
                    { key: 'cms', match: cms.id },
                    { key: 'category_keys', in: categories.map(category => category.key) }
                ],
                variables: { order: 'created_at', direction: 'desc', withCategory: true }
            })
                .then(data => (data && data.items) || [])
                .catch(() => [])
        }
    }
}
